import { SIDE_NAMES, SINK_SENTENCES, sinkReason, WARRIOR_NAMES } from '@halvard/board3d';
import { ice, squareName } from '@halvard/rules';

const name = (sq: ice.Square) =>
  sq.r >= 0 && sq.r <= 8 && sq.c >= 0 && sq.c <= 8 ? squareName(sq.r, sq.c) : 'off the ice';

export function warriorName(w: Pick<ice.Warrior, 'side' | 'type'>): string {
  return `${SIDE_NAMES[w.side]} ${WARRIOR_NAMES[w.type]}`;
}

/** "3 against 2 (2 force + 1 shield against 1 force + 1 shield)". */
export function shoveSums(
  shover: ice.Warrior,
  target: ice.Warrior,
  strength: number,
  wall: number,
): string {
  const part = (force: number, total: number) => {
    const shields = total - force;
    return shields > 0 ? `${force} force + ${shields} shield` : `${force} force`;
  };
  return `${strength} against ${wall} (${part(ice.FORCE[shover.type], strength)} against ${part(ice.FORCE[target.type], wall)})`;
}

/** Sentences for the move log and screen readers, from the state before the action. */
export function describeIceEvents(before: ice.IceState, events: readonly ice.IceEvent[]): string[] {
  const find = (id: string) => before.warriors.find((w) => w.id === id);
  const lines: string[] = [];
  const cracked: string[] = [];
  for (const event of events) {
    switch (event.kind) {
      case 'placed':
        lines.push(`${warriorName(event.warrior)} takes position on ${name(event.warrior)}.`);
        break;
      case 'moved': {
        const w = find(event.id);
        const from = event.path[0];
        const to = event.path.at(-1);
        if (w && from && to) lines.push(`${warriorName(w)} moves ${name(from)} to ${name(to)}.`);
        break;
      }
      case 'shoved': {
        const w = find(event.id);
        const first = event.slides.at(-1);
        const target = first && find(first.id);
        if (!w || !target || !first) break;
        lines.push(
          `${warriorName(w)} shoves ${warriorName(target)} on ${name(first.from)}: ${shoveSums(w, target, event.strength, event.wall)}.`,
        );
        for (const slide of event.slides) {
          const slid = find(slide.id);
          const reason = sinkReason(before, slide);
          if (slid && reason) lines.push(`${warriorName(slid)} ${SINK_SENTENCES[reason]}.`);
        }
        break;
      }
      case 'passed':
        lines.push(`${SIDE_NAMES[event.side]} cannot move and passes.`);
        break;
      case 'glory':
        if (event.gained > 0)
          lines.push(`Brand draws ${event.gained} Glory (${event.total} in all).`);
        break;
      case 'thawed': {
        const sq = name(event);
        const sunk = event.sunk ? find(event.sunk) : undefined;
        lines.push(
          event.height === ice.HOLE
            ? `The ice on ${sq} gives way${sunk ? `: ${warriorName(sunk)} goes under` : ''}.`
            : `The ice on ${sq} thins to ${event.height}.`,
        );
        break;
      }
      case 'cracked':
        cracked.push(name(event));
        break;
      case 'over':
        lines.push(outcomeText(event.winner, event.reason).title);
        break;
    }
  }
  if (cracked.length > 0) lines.push(`The ice groans: ${cracked.join(' and ')} crack.`);
  return lines;
}

export function outcomeText(
  winner: ice.Side,
  reason: ice.IceWinReason,
): { title: string; detail: string } {
  switch (reason) {
    case 'glory':
      return { title: 'Brand wins', detail: 'Brand drew enough power from the runestones.' };
    case 'held':
      return { title: 'Rime wins', detail: 'Rime held the lake until the thaw.' };
    case 'sunk':
      return {
        title: `${SIDE_NAMES[winner]} wins`,
        detail: `Every ${winner === 'brand' ? 'Rime' : 'Brand'} warrior went under.`,
      };
    case 'resign':
      return {
        title: `${SIDE_NAMES[winner]} wins`,
        detail: `${winner === 'brand' ? 'Rime' : 'Brand'} resigned.`,
      };
  }
}
